import React, { useState, useEffect } from "react";
import {
	Select,
	MenuItem,
	InputLabel,
	FormControl,
	Box,
	CircularProgress,
	Autocomplete,
	TextField,
} from "@mui/material";
import { CustomDropdown } from "./inputs";
import { useTranslation } from "react-i18next";
import { NeutralColor, PrimaryColor } from "node-library/dist/colors";

interface Props {
	currencyDropdownId: string;

	selectedDisplayCurrency: string;
	setSelectedDisplayCurrency: React.Dispatch<React.SetStateAction<string>>;
	disabled?: boolean;
	setUnsavedChanges?: React.Dispatch<React.SetStateAction<boolean>>;
}

interface Currency {
	name: string;
	code: string;
	symbol: string;
	symbol_native: string;
	name_plural: string;
}

interface DisplayCurrency {
	code: string;
	symbol: string;
}

export default function CurrencySelector(props: Props) {
	const [currencies, setCurrencies] = useState<Array<Currency>>([]); // Country data
	const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(
		null
	);

	const [loading, setLoading] = useState<boolean>(true); // Loading state

	const {
		currencyDropdownId,
		selectedDisplayCurrency,
		setSelectedDisplayCurrency,
		disabled,
		setUnsavedChanges,
	} = props;

	const { t: translation } = useTranslation();

	// Fetch countries and provinces data from the JSON file located in the public folder
	useEffect(() => {
		fetch("/currency.json")
			.then((response) => response.json())
			.then((data: Currency[]) => {
				setCurrencies(data); // Set the countries data
				if (!selectedDisplayCurrency) {
					setSelectedDisplayCurrency(data[2].code);
					setSelectedCurrency(data[2]);
				} else {
					setSelectedCurrency(
						data.find((currency) => {
							return currency.code === selectedDisplayCurrency;
						}) || data[2]
					);
				}

				setLoading(false); // Stop loading once data is fetched
			})
			.catch((error) => {
				console.error("Error loading the currency data:", error);
				setLoading(false); // Stop loading in case of an error
			});
	}, []);

	// Handle currency selection change
	function handleCurrencyChange(displayCurrency: string) {
		setSelectedDisplayCurrency(displayCurrency);
		if (setUnsavedChanges) {
			setUnsavedChanges(true);
		}
		const currency: Currency | undefined = currencies.find((currency) => {
			return currency.code === displayCurrency;
		});
		if (currency) {
			setSelectedCurrency(currency);
		}
	}

	return (
		<>
			<Autocomplete
				disablePortal
				id={currencyDropdownId}
				options={currencies}
				value={selectedCurrency}
				onChange={(event, newValue) => {
					if (newValue) {
						handleCurrencyChange(newValue.code);
					}
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						label=""
						sx={{
							"& .MuiOutlinedInput-root": {
								padding: "4px 9px", // Custom padding for input field

								"&:hover": {
									borderColor: PrimaryColor.PRIMARY_800, // Change border color on hover
								},
								//   '&.Mui-focused': {
								//     borderColor: 'red', // Change border color when focused
								//   },
							},
							"& .MuiOutlinedInput-root fieldset": {
								borderColor: NeutralColor.NEUTRAL_GREY_900, // Change border color
							},
						}}
					/>
				)}
				getOptionLabel={(option) => {
					return option ? `${option.code} (${option.symbol})` : option;
				}}
				isOptionEqualToValue={(option, value) => option.code === value.code}
				disabled={disabled}
			/>
		</>
	);
}
